"use client";

import Button from "@afa-shared/afa-components/dist/Buttons/Button";
import Heading from "@afa-shared/afa-components/dist/Heading";
import TextInput from "@afa-shared/afa-components/dist/Inputs/TextInput";
import Text from "@afa-shared/afa-components/dist/Text";
import { StandardLink } from "@components/Link/StandardLink";
import Grid from "@components/client/Grid";
import clsx from "clsx";
import { useId, useRef, useState } from "react";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";

import FormErrorFeedback from "../Feedback/FormErrorFeedback";
import { FormSendFeedback } from "../Feedback/FormSendFeedback";
import FormSuccessFeedback from "../Feedback/FormSuccessFeedback";
import formStyles from "../FormStyles/forms.module.css";
import { emailMaxInputLength } from "../formConstants";
import { scrollIntoView, validate } from "../formHelper";
import { trackFormStart, trackFormSubmit } from "../formTracker";
import newsletterStyles from "./newsletterForm.module.css";

const testFormIds = {
  "Nyhetsbrevsanmälan-Trygg på jobbet": "73043510-6e21-ef11-840a-000d3aa9aa46",
  "Nyhetsbrevsanmälan-Inbjudningar till frukostseminarier": "006adc46-6e21-ef11-840a-000d3aa9aa46",
  "Nyhetsbrevsanmälan-Statistikrapporter": "511251a1-6e21-ef11-840a-000d3aa9aa46",
  "Nyhetsbrevsanmälan-Arbetsmiljö och hälsa": "d7e74516-c83a-ef11-8409-000d3a21a1de",
};

const prodFormIds = {
  "Nyhetsbrevsanmälan-Trygg på jobbet": "89563ce5-7713-ef11-9f89-7c1e52126257",
  "Nyhetsbrevsanmälan-Inbjudningar till frukostseminarier": "8fa966f5-3b1f-ef11-840b-7c1e52126257",
  "Nyhetsbrevsanmälan-Statistikrapporter": "bf14dc4b-9808-ef11-9f89-6045bd92e338",
  "Nyhetsbrevsanmälan-Arbetsmiljö och hälsa": "48796298-9a75-ef11-a670-000d3a49d798",
};

const gaTitle = {
  "Nyhetsbrevsanmälan-Trygg på jobbet": "Trygg på jobbet",
  "Nyhetsbrevsanmälan-Inbjudningar till frukostseminarier": "seminarier",
  "Nyhetsbrevsanmälan-Statistikrapporter": "statistikrapporter från Afa Försäkring",
  "Nyhetsbrevsanmälan-Arbetsmiljö och hälsa": "Arbetsmiljö och hälsa",
};

const getMailingListId = (formId: string): string => {
  const testEnvironment = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT === "test";

  if (testEnvironment) return testFormIds[formId];
  return prodFormIds[formId];
};

interface INewsletterForm {
  formId: string;
  title: string;
  description: string;
  simpleVersion: boolean;
  contentfulPreviewEntryId?: string;
}

export const NewsletterForm = ({
  formId,
  title,
  description,
  simpleVersion,
  contentfulPreviewEntryId,
}: INewsletterForm) => {
  const [formStarted, setFormStarted] = useState(false);
  const [displayForm, setDisplayForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isValidToken, setIsValidToken] = useState<boolean>(false);
  const [errorSendMail, setErrorSendMail] = useState<boolean>(false);
  const [formState, setFormState] = useState({
    email: { value: "", required: true },
  });

  const mailingListId = getMailingListId(formId);

  const policyText = "Så behandlar vi personuppgifter";
  const policyLink = "/personuppgifter";

  const [errors, setErrors] = useState<any>({
    email: null,
  });

  const formRefs = {
    sendFeedback: useRef(null),
    errorInfobox: useRef(null),
    email: useRef(null),
    approved: useRef(null),
  };

  const formIds = {
    email: useId(),
    header: useId(),
  };

  const handleFormChange = (e: any, key: string = null) => {
    const { value } = e.target;
    if (!formStarted) {
      trackFormStart("nyhetsbrev", gaTitle[formId]?.toLowerCase());
      setFormStarted(true);
    }
    setFormState({ ...formState, [key]: { ...formState[key], value: value } });
    setErrors({ ...errors, [key]: null });
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    const formErrors = validate(formState, errors);
    setErrors(formErrors);
    const foundErrors = Object.values(formErrors).filter((e) => e !== null).length;

    if (foundErrors) {
      scrollIntoView(formRefs?.email);
    } else {
      setIsLoading(true);
      try {
        const res = await fetch(`/api/manageSubscriptions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user: {
              emailAddress: formState.email.value,
            },
            subscriptions: [
              {
                messageId: "1",
                action: "subscribe",
                listId: mailingListId,
              },
            ],
          }),
        });

        if (res.ok) {
          setDisplayForm(true);
          formRefs?.sendFeedback?.current?.focus();
          trackFormSubmit("nyhetsbrev", gaTitle[formId]?.toLowerCase());
        } else {
          setErrorSendMail(true);
          setDisplayForm(true);
          formRefs?.sendFeedback?.current?.focus();
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleReCaptchaVerify = (token) => {
    if (!token) {
      return;
    }
    token && setIsValidToken(true);
  };

  const renderFormFeedback = () => (
    <FormSuccessFeedback
      messageHeading="Din information har skickats"
      messageChildren={
        <Text variant="paragraph" removeMargin>
          Du får strax ett mejl från oss. Kontrollera att det inte fastnar i skräppostfiltret.
        </Text>
      }
      messageLink={<StandardLink text={policyText} href={policyLink} />}
    />
  );

  const renderErrorMessage = () => <FormErrorFeedback />;

  const showFormFeedback: boolean = displayForm && !errorSendMail;
  return (
    <div
      className="form-container"
      data-contentful-entry-id={contentfulPreviewEntryId}
      data-contentful-field-id="name"
    >
      <Grid container className={newsletterStyles.newsletterFormWrapper}>
        {!simpleVersion && (
          <Grid
            mobile={12}
            tablet={12}
            desktop={6}
            position="relative"
            className={newsletterStyles.newsletterImageWrapper}
          ></Grid>
        )}
        <Grid
          mobile={12}
          tablet={12}
          desktop={6}
          className={newsletterStyles.newsletterContentWrapper}
          wrap="nowrap"
        >
          <Heading id={formIds?.header} variant="h2" removeMargin>
            {title ? title : "Prenumerera på nyhetsbrevet"}
          </Heading>
          {displayForm && (
            <FormSendFeedback
              formSendRef={formRefs?.sendFeedback}
              formErrorFeedback={renderErrorMessage()}
              formSuccessFeedback={renderFormFeedback()}
              showSuccessFeedback={showFormFeedback}
              showErrorFeedback={errorSendMail}
            />
          )}

          {!displayForm && (
            <>
              <GoogleReCaptcha onVerify={(token) => handleReCaptchaVerify(token)} />

              <form
                noValidate
                aria-labelledby={formIds?.header}
                className={`${formStyles.form} ${newsletterStyles.newsletterForm}`}
              >
                <Grid direction="column" justifyContent="space-between" width="100%" wrap="nowrap">
                  {description && (
                    <div className={newsletterStyles.formDescription}>
                      <Text variant="span">{description}</Text>
                    </div>
                  )}

                  <div className={newsletterStyles.inputWrapper}>
                    <TextInput
                      inputRef={formRefs?.email}
                      label={"Din e-postadress (kontrollera stavningen)*"}
                      id={formIds?.email}
                      autoComplete="email"
                      value={formState?.email?.value}
                      type={"email"}
                      maxLength={emailMaxInputLength}
                      width="100%"
                      error={errors?.email?.input}
                      onChange={(e) => handleFormChange(e, "email")}
                    />
                  </div>
                  <div className={newsletterStyles.termsLinkWrapper}>
                    <StandardLink text={policyText} href={policyLink} />
                  </div>
                </Grid>
                <Button
                  type="submit"
                  buttonType="primary"
                  onClick={
                    isValidToken
                      ? onSubmit
                      : (e) => {
                          e.preventDefault();
                        }
                  }
                  margin="0 0 0 0"
                  label={isLoading ? "" : "Skicka"}
                  className={clsx(newsletterStyles.newsletterSubmitButton, {
                    [newsletterStyles.spinner]: isLoading,
                  })}
                  disabled={isLoading}
                />
              </form>
            </>
          )}
        </Grid>
      </Grid>
    </div>
  );
};
export default NewsletterForm;
